import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";
import { useToast } from "../../../hooks/use-toast";
import { suporteService } from "../services/suporte-service";
import { AvisoFiltro, AvisoLote } from "../types";
import { suporteKeys } from "./use-suporte";

const mensagemErro = (error: any) => error?.response?.data?.message || error?.message || "Erro inesperado.";

export function useAvisoMenus() {
  return useQuery({
    queryKey: [...suporteKeys.all, "aviso-menus"],
    queryFn: async () => (await suporteService.avisoMenus()).data.items,
    staleTime: 5 * 60_000,
  });
}

// Conta quantos atendimentos receberiam o aviso. Não escreve nada.
export function usePreviaAviso() {
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (filtro: AvisoFiltro) => (await suporteService.avisoPrevia(filtro)).data,
    onError: (error: any) =>
      toast({ title: "Erro ao contar os atendimentos", description: mensagemErro(error), variant: "destructive" }),
  });
}

export interface EstadoEnvio {
  fase: "parado" | "enviando" | "concluido";
  processados: number;
  encerrados: number;
  ignorados: number;
  falhas: AvisoLote["failed"];
  parouAntes: boolean; // o atendente interrompeu
  erro?: string;
}

const INICIAL: EstadoEnvio = { fase: "parado", processados: 0, encerrados: 0, ignorados: 0, falhas: [], parouAntes: false };

// Envia o aviso em lotes, em sequência, até o chatwoot dizer que não há mais
// tickets. Parar interrompe depois do lote em andamento; mandar de novo com o
// mesmo aviso só alcança o que sobrou, e nunca repete a mensagem num ticket.
export function useEnviarAviso() {
  const queryClient = useQueryClient();
  const [estado, setEstado] = useState<EstadoEnvio>(INICIAL);
  const interromper = useRef(false);
  const ocupado = useRef(false);

  const iniciar = useCallback(
    async (filtro: AvisoFiltro, body: string) => {
      if (ocupado.current) return;
      ocupado.current = true;
      interromper.current = false;
      const broadcastId = crypto.randomUUID();
      let acc: EstadoEnvio = { ...INICIAL, fase: "enviando" };
      setEstado(acc);
      let cursor: string | null = null;
      try {
        do {
          const { data: lote }: { data: AvisoLote } = await suporteService.avisoEnviar(filtro, body, broadcastId, cursor);
          acc = {
            ...acc,
            processados: acc.processados + lote.processed,
            encerrados: acc.encerrados + lote.closed,
            ignorados: acc.ignorados + lote.skipped,
            falhas: [...acc.falhas, ...lote.failed],
          };
          setEstado(acc);
          cursor = lote.next_cursor;
        } while (cursor && !interromper.current);
        setEstado({ ...acc, fase: "concluido", parouAntes: Boolean(cursor) });
      } catch (error) {
        setEstado({ ...acc, fase: "concluido", parouAntes: true, erro: mensagemErro(error) });
      } finally {
        ocupado.current = false;
        queryClient.invalidateQueries({ queryKey: suporteKeys.all });
      }
    },
    [queryClient]
  );

  const parar = useCallback(() => {
    interromper.current = true;
  }, []);

  const reiniciar = useCallback(() => setEstado(INICIAL), []);

  return { estado, iniciar, parar, reiniciar };
}
